import { useTranslation } from 'next-i18next/pages'
import { InjectionTariffProps } from './types'
import { Button, CallToAction, Header, Heading, HeadingAndContent, List, TextAndMedia } from '@boltenergy-be/design-system'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import BoltOffering from 'sections/bolt-offering/BoltOffering'
import parse from 'html-react-parser'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { formatAmount } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { I18nUSP } from 'types/i18n'
import InjectionSimulationForm from 'features/injection-tariff/components/injection-simulation-form/InjectionSimulationForm'
import ReviewSection from 'sections/review-section/ReviewSection'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'

const InjectionTariff = ({ faqCategories, communityMetrics, initStoreForInjectionTariff, setSimulatedInjection }: InjectionTariffProps) => {
  // i18n
  const { t } = useTranslation(['injection', 'common'])
  const language = useGetLanguage()
  return (
    <main>
      {/* HERO */}
      <Header title={t('header.title')} docked shape="s3">
        <p>{t('header.description')}</p>
      </Header>

      <InjectionSimulationForm {...{ setSimulatedInjection, initStoreForInjectionTariff }} />

      {/* REVIEWS */}
      <ReviewSection title={t('reviews.title')} description={t('reviews.description')} />

      {/* HOW IT WORKS */}
      <TextAndMedia
        title={t('howItWorks.title')}
        media={{ type: 'image', props: { publicId: 'website/injection-tariff/how', alt: '', width: 600, height: 800 } }}
      >
        <p>{parse(t('howItWorks.paragraph1'))}</p>
        <p>{t('howItWorks.paragraph2')}</p>
        <StartSimulationButton extraOnClickAction={() => initStoreForInjectionTariff()} fromPage="injection-tariff" />
      </TextAndMedia>

      {/* BOLT ADVANTAGES */}
      <HeadingAndContent title={t('advantages.title')} color="accent-warm" inset>
        <List as="ul" bullets="badgeCheck">
          {(t('advantages.bullets', { returnObjects: true }) as string[]).map((item: string, index: number) => (
            <li key={`advantage-${index}`}>{item}</li>
          ))}
        </List>
      </HeadingAndContent>

      {/* B2B */}
      <HeadingAndContent title={t('b2b.title')}>
        <p>{parse(t('b2b.description'))}</p>
        <Link representation="button" trailingIcon="chevronRight" href={routes(language).becomeProducer}>
          {t('b2b.action')}
        </Link>
      </HeadingAndContent>

      {/* OFFERING */}
      <BoltOffering
        title={t('whyBolt.title')}
        steps={t('whyBolt.advantages', { returnObjects: true }) as Array<I18nUSP>}
        customImage="website/injection-tariff/advantages"
      />

      {/* TOTAL INJECTION */}
      <CallToAction
        title={t('currentTotal.title', {
          injection: formatAmount(
            communityMetrics?.miniProducers?.totalInjection ? Math.round(communityMetrics?.miniProducers?.totalInjection / 1000) : 32_576,
            language
          )
        })}
        titleVariant="d3"
        serifTitle={false}
        color="accent-soft"
        shape="s1"
      >
        <Heading as="h2" variant="h3" serif>
          {t('currentTotal.description', { households: formatAmount(communityMetrics?.miniProducers?.households || 9_307, language) })}
        </Heading>
      </CallToAction>

      {/* CALL TO ACTION */}
      <HeadingAndContent title={t('cta.title')} color="dark" shape="s2">
        <p>{t('cta.description')}</p>
        <Button
          onClick={() => {
            initStoreForInjectionTariff()
            startSimulationOrRegistration({ fromPage: 'injection-tariff' })
          }}
        >
          {t('common:calculateProposal')}
        </Button>
      </HeadingAndContent>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faqCategories} title={t('faq.title')} />
    </main>
  )
}

export default InjectionTariff
